import { cn } from '@rocco/utils/cn';
import {
    button,
    type ButtonKind,
    type ButtonSize,
} from '~/ui-rocco/button-system';

/**
 * The detail-page CTA, expressed in the platform button system.
 *
 * The variant names are kept because a lot of call sites across the product,
 * project and professional pages pass them; each one now resolves to a kind in
 * `button-system.ts` rather than carrying its own colours and geometry. That is
 * the whole point of the migration — one ramp, one set of roles.
 */
export type DetailCtaVariant =
    | 'filled-light' // dark pill on a light section (Get in touch)
    | 'outlined-light' // hairline pill on a light section (Visit Website)
    | 'filled-dark' // primary on a dark section (Book a Meeting)
    | 'outlined-dark' // hairline pill on a dark section (Send Enquiry)
    | 'filled-invert' // solid white on a dark section (closing prompt CTA)
    | 'grey-dark' // was #2d2d2d — a fourth near-black, now the one ink
    | 'white-lifted' // white on the page's own ground (product detail)
    | 'glass'; // a translucent pane on the page's own ground

/**
 * On a dark section the primary action is white, not a lighter grey: the old
 * `filled-dark` (#333333 on near-black) and `grey-dark` (#2d2d2d) were two of
 * the eight near-blacks the audit found, and neither read as primary.
 */
const VARIANT_KIND: Record<DetailCtaVariant, ButtonKind> = {
    'filled-light': 'primary',
    'outlined-light': 'secondary',
    'filled-dark': 'primary-inverse',
    'outlined-dark': 'secondary-dark',
    'filled-invert': 'primary-inverse',
    'grey-dark': 'primary',
    'white-lifted': 'lifted',
    glass: 'glass',
};

/** Detail-page CTAs sit at L (52px) unless a caller asks for another step. */
const DEFAULT_SIZE: ButtonSize = 'l';

/**
 * Marker class kept as a styling hook for page-scoped overrides. It no longer
 * carries geometry — height, corner and padding come from the size.
 */
const HOOK = 'detail-cta-pill';

export const detailCta = (
    variant: DetailCtaVariant,
    extra?: string,
    size: ButtonSize = DEFAULT_SIZE,
) => cn(HOOK, button(VARIANT_KIND[variant], size, extra));

/** The matching square (icon-only) button, e.g. the Save / bookmark control. */
export const detailCtaIcon = (
    variant: DetailCtaVariant,
    extra?: string,
    size: ButtonSize = DEFAULT_SIZE,
) => cn(HOOK, button(VARIANT_KIND[variant], size, extra, { icon: true }));

/**
 * Retained for callers that size their own icon button. Prefer
 * {@link detailCtaIcon}, which takes its geometry from the ramp.
 */
export const DETAIL_CTA_ICON_SIZE = 'size-[3.25rem] rounded-[1.375rem]';
